import { buildConnection } from '$app/arrp-hub-connection-builder'
import apiConnection from '$app/plugins/api-connection'
import defaultPagingInfo from '$app/default-paging-info'

const enrollmentConnection = buildConnection('Enrollment');
const flkrsConnection = buildConnection('StagingFlkrs');

// STATE
const state = {
  enrollmentLoading: false,
  lastNameLetter: 'A',
  enrollmentSkip: 0,
  skipDirection: 1,
  unmatchedEnrollment: {},
  unmatchedEnrollmentCount: 0,
  enrollmentLocks: [],
  flkrsLoading: false,
  items: [],
  flkrsLocks: [],
  totalItems: 0,
  undoQueue: [],
  pagingInfo: {
    ...defaultPagingInfo(),
    sortBy: 'lastName'
  },
  searchModel: {
    applyNameFilters: true,
    nameSearchType: 'soundex',
    firstName: '',
    lastName: '',
    firstNameSoundex: 3,
    lastNameSoundex: 3,
    includeTransposedLastName: true,
    applyDobFilters: false,
    dobSearchType: 'exact',
    applyGenderFilters: false,
    genderSearchType: 'exact'
  }
}

// MUTATIONS
const mutations = {
  updateSearchModel(state, searchModel) {
    state.searchModel = searchModel;
    state.pagingInfo.page = 1;
  },
  updateItems(state, items) {
    state.items = items.map(i => ({ ...i, studentId: i.studentId || i.localId }));
  },
  updatePagingInfo(state, pagingInfo) {
    state.pagingInfo = { ...pagingInfo };
  },
  setStateProp(state, obj) {
    state[obj.name] = obj.value;
  },
  pushUndoQueue(state, flkrsId) {
    const { undoQueue, ...snapshot } = state;
    state.undoQueue.push({ flkrsId, ...snapshot });
  },
  popUndoQueue(state) {
    const snapshot = state.undoQueue.pop();
    Object.assign(state, snapshot);
  },
  setContainsFilters(state) {
    state.searchModel.firstName = state.unmatchedEnrollment.modifiedOldFirstName;
    state.searchModel.lastName = state.unmatchedEnrollment.modifiedOldLastName;
  }
}

// ACTIONS
const actions = ({
  async connect({ commit, dispatch }) {
    enrollmentConnection.onLocksUpdated((locks) => {
      commit('setStateProp', { name: 'enrollmentLocks', value: locks });
    });
    flkrsConnection.onLocksUpdated((locks) => {
      commit('setStateProp', { name: 'flkrsLocks', value: locks });
    });

    enrollmentConnection.onEntitiesUpdated(() => dispatch('getUnmatchedEnrollment'));
    flkrsConnection.onEntitiesUpdated(() => dispatch('getUnmatchedEnrollment'));

    await enrollmentConnection.connect();
    await flkrsConnection.connect();
  },
  async disconnect() {
    await enrollmentConnection.disconnect();
    await flkrsConnection.disconnect();
  },
  lockEnrollment(x, pcamId) {
    enrollmentConnection.lockById(pcamId);
  },
  unlockEnrollment(x, pcamId) {
    enrollmentConnection.unlockById(pcamId);
  },
  jumpToEnrollment({ commit, dispatch }, params) {
    commit('setStateProp', { name: 'enrollmentLoading', value: true });
    apiConnection.get('api/arrp/LocateEnrollmentForManualMatch', { params })
      .then(resp => {
        if (resp && resp.data) {
          commit('setStateProp', { name: 'enrollmentSkip', value: resp.data.skip });
          commit('setStateProp', { name: 'lastNameLetter', value: resp.data.lastNameLetter });
          dispatch('getUnmatchedEnrollment');
        }
      });
  },
  getUnmatchedEnrollment({ commit, state, dispatch }) {
    const prevEnrollment = state.unmatchedEnrollment;
    dispatch('unlockEnrollment', prevEnrollment.pcamId);
    commit('setStateProp', { name: 'enrollmentLoading', value: true });
    const tryGetEnrollment = () => {
      const { lastNameLetter, enrollmentSkip } = state;
      apiConnection.get('api/arrp/GetUnmatchedEnrollment', { params: { lastNameLetter, skip: enrollmentSkip } })
        .then(resp => {
          if (resp && resp.data && resp.data.totalItems != null) {
            commit('setStateProp', { name: 'unmatchedEnrollmentCount', value: resp.data.totalItems });
            if (resp.data.items.length) {
              const newEnrollment = resp.data.items[0];
              if (state.enrollmentLocks.includes(newEnrollment.pcamId)) {
                if (state.skipDirection > 0 || state.enrollmentSkip > 0) {
                  commit('setStateProp', { name: 'enrollmentSkip', value: state.enrollmentSkip + state.skipDirection });
                } else {
                  commit('setStateProp', { name: 'skipDirection', value: 1 });
                  commit('setStateProp', { name: 'enrollmentSkip', value: state.enrollmentSkip + state.skipDirection });
                }
                tryGetEnrollment();
              } else {
                localStorage.setItem('manualMatch:pcamId', newEnrollment.pcamId);
                localStorage.removeItem('manualMatch:enrollmentId');
                commit('setStateProp', { name: 'unmatchedEnrollment', value: newEnrollment });
                commit('setContainsFilters');
                commit('updatePagingInfo', { ...state.pagingInfo, page: 1 });
                dispatch('lockEnrollment', newEnrollment.pcamId);
                dispatch('searchFlkrs');
                commit('setStateProp', { name: 'enrollmentLoading', value: false });
              }
            }
          }
        })
        .catch(() => {
          this._vm.$snackbar('Unable to Load Enrollment', 'error');
          commit('setStateProp', { name: 'enrollmentLoading', value: false });
        });
    }
    tryGetEnrollment();
  },
  searchFlkrs({ commit, state }) {
    const searchModel = {
      ...state.searchModel,
      pcam: { ...state.unmatchedEnrollment },
      pagingInfo: state.pagingInfo
    };

    if (searchModel.pcam.pcamId) {
      commit('setStateProp', { name: 'flkrsLoading', value: true });
      apiConnection.post('api/arrp/FindPotentialFlkrsMatches', searchModel).then(resp => {
        commit('updateItems', resp.data.items);
        commit('setStateProp', { name: 'totalItems', value: resp.data.totalItems });
      }).finally(() => commit('setStateProp', { name: 'flkrsLoading', value: false }));
    }
  },
  resetEnrollmentSearch({ commit, dispatch }, lastNameLetter) {
    commit('setStateProp', { name: 'enrollmentSkip', value: 0 });
    commit('setStateProp', { name: 'lastNameLetter', value: lastNameLetter });
    dispatch('getUnmatchedEnrollment');
  },
  incEnrollmentSkip({ commit, state, dispatch }) {
    commit('setStateProp', { name: 'skipDirection', value: 1 });
    dispatch('doSkip');
  },
  decEnrollmentSkip({ commit, state, dispatch }) {
    if (state.enrollmentSkip > 0) {
      commit('setStateProp', { name: 'skipDirection', value: -1 });
      dispatch('doSkip');
    }
  },
  doSkip({ commit, state, dispatch }) {
    if (state.skipDirection > 0 || state.enrollmentSkip > 0) {
      commit('setStateProp', { name: 'enrollmentSkip', value: state.enrollmentSkip + state.skipDirection });
      dispatch('getUnmatchedEnrollment');
    }
  },
  createMatch({ commit, state, dispatch }, flkrs) {
    return new Promise((resolve, reject) => {
      const { pcamId } = state.unmatchedEnrollment;
      const flkrsId = flkrs.stagingFlkrsid;

      apiConnection.post('api/arrp/ApplyManualMatch', { pcamId, flkrsId }).then(resp => {
        if (resp && resp.data && resp.data.success) {
          commit('pushUndoQueue', flkrsId);
          resolve();
        } else {
          reject(new Error('Unable to Create Manual Match'));
        }
      });
    });
  },
  undoMatch({ commit, state, dispatch }) {
    if (state.undoQueue.length) {
      const snapshot = state.undoQueue[state.undoQueue.length - 1];
      const { flkrsId, unmatchedEnrollment: { pcamId } } = snapshot;

      apiConnection.post('api/arrp/UndoManualMatch', { pcamId, flkrsId }).then(resp => {
        if (resp && resp.data && resp.data.success) {
          commit('popUndoQueue');
        } else {
          this._vm.$snackbar('Unable to Undo Manual Match', 'error');
        }
      });
    }
  },
  sortItems({ commit, dispatch }, sortValues) {
    const pInfo = { ...state.pagingInfo, page: 1, ...sortValues };
    commit('updatePagingInfo', pInfo);
    dispatch('searchFlkrs');
  },
  pagingInfoUpdated({ commit, dispatch }, pagingInfo) {
    commit('updatePagingInfo', pagingInfo);
    dispatch('searchFlkrs');
  },
  getPagedData({ commit, dispatch, state }, page) {
    commit('updatePagingInfo', { ...state.pagingInfo, page });
    dispatch('searchFlkrs');
  },
  updateSearchModel({ commit, dispatch }, searchModel) {
    commit('updateSearchModel', searchModel);
    dispatch('searchFlkrs');
  }
});

// GETTERS
const getters = {
  searchModel: state => state.searchModel,
  itemsToLinkToEnrollment: state => state.itemsToLinkToEnrollment,
  flkrsItems: state => state.items.map(item => ({ ...item, locked: state.flkrsLocks.includes(item.stagingFlkrsid) }))
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
